import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Calendar, CheckCircle2, Clock, ChevronDown, DollarSign } from 'lucide-react';
import { formatDate as formatDateUtil, formatDateShort, parseDate as parseDateUtil } from '@/lib/dateUtils';

// Helper to parse date string without timezone issues
const parseDate = (dateStr) => {
  return parseDateUtil(dateStr);
};

// Format date for display (MM/DD/YYYY)
const formatDateFull = (dateStr) => {
  if (!dateStr) return 'Not set';
  return formatDateUtil(dateStr);
};

// Format date for short display (Jan 15)
const formatDate = (date) => {
  if (!date) return '-';
  // For Date objects, format as "Jan 15"
  if (date instanceof Date) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  }
  return formatDateShort(date);
};

const GanttChart = ({ phases, readOnly = true, contractors = [], project = {} }) => {
  const [hoveredPhase, setHoveredPhase] = useState(null);
  const [expandedPhases, setExpandedPhases] = useState({});

  // Toggle expanded state for a phase
  const togglePhaseExpanded = (phaseId) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };

  // Get phase status for display
  const getPhaseStatus = (phase) => {
    if (phase.is_paid || phase.status === 'completed') return 'completed';
    if (phase.status === 'in_progress') return 'in_progress';
    if (phase.start_date) {
      const start = new Date(phase.start_date);
      const today = new Date();
      if (start <= today) return 'in_progress';
      return 'scheduled';
    }
    return 'pending';
  };

  // Filter phases with dates
  const phasesWithDates = useMemo(() => {
    return phases
      .filter(phase => phase.start_date)
      .map(phase => {
        const startDate = parseDate(phase.start_date);
        let endDate = phase.end_date ? parseDate(phase.end_date) : new Date(startDate);
        
        if (!phase.end_date) {
          const daysToAdd = phase.phase_type === 'labor' ? 14 : 7;
          endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + daysToAdd);
        }

        return {
          ...phase,
          startDate,
          endDate,
        };
      })
      .sort((a, b) => a.startDate - b.startDate);
  }, [phases]);

  // Calculate date range for 12 months
  const monthsData = useMemo(() => {
    if (phasesWithDates.length === 0) return [];
    
    const minDate = new Date(Math.min(...phasesWithDates.map(p => p.startDate)));
    const startMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
    
    const months = [];
    for (let i = 0; i < 12; i++) {
      const month = new Date(startMonth);
      month.setMonth(month.getMonth() + i);
      months.push({
        date: month,
        label: month.toLocaleDateString('en-US', { month: 'short' }),
        year: month.getFullYear()
      });
    }
    
    return months;
  }, [phasesWithDates]);

  // Calculate bar position for each phase (returns pixel values based on month width)
  const getBarPosition = (phase, monthWidth) => {
    if (monthsData.length === 0) return { left: 0, width: 0 };
    
    const startMonth = monthsData[0].date;
    const endMonth = new Date(monthsData[11].date);
    endMonth.setMonth(endMonth.getMonth() + 1);
    
    const totalMs = endMonth.getTime() - startMonth.getTime();
    const totalWidth = monthWidth * 12;
    
    const phaseStartMs = Math.max(phase.startDate.getTime() - startMonth.getTime(), 0);
    const phaseEndMs = Math.min(phase.endDate.getTime() - startMonth.getTime(), totalMs);
    
    const left = (phaseStartMs / totalMs) * totalWidth;
    const width = ((phaseEndMs - phaseStartMs) / totalMs) * totalWidth;
    
    return { 
      left: Math.max(0, left), 
      width: Math.max(60, Math.min(totalWidth - left, width))
    };
  };

  // Get phase color based on status
  const getPhaseColor = (phase) => {
    if (phase.status === 'completed' || phase.is_paid) return 'from-green-500 to-green-600';
    if (phase.status === 'delayed') return 'from-red-500 to-red-600';
    if (phase.status === 'in_progress') return 'from-amber-500 to-amber-600';
    if (phase.phase_type === 'material') return 'from-purple-500 to-purple-600';
    return 'from-blue-500 to-blue-600';
  };

  // Get status label
  const getStatusLabel = (phase) => {
    if (phase.status === 'completed' || phase.is_paid) return { text: '✓ Completed', color: 'text-green-400', icon: '✓' };
    if (phase.status === 'delayed') return { text: '! Delayed', color: 'text-red-400', icon: '!' };
    if (phase.status === 'in_progress') return { text: '→ In Progress', color: 'text-amber-400', icon: '→' };
    return { text: '○ Pending', color: 'text-blue-400', icon: '' };
  };

  // Today line position
  const getTodayPosition = (monthWidth) => {
    if (monthsData.length === 0) return null;
    
    const today = new Date();
    const startMonth = monthsData[0].date;
    const endMonth = new Date(monthsData[11].date);
    endMonth.setMonth(endMonth.getMonth() + 1);
    
    if (today < startMonth || today > endMonth) return null;
    
    const totalMs = endMonth.getTime() - startMonth.getTime();
    const todayMs = today.getTime() - startMonth.getTime();
    const totalWidth = monthWidth * 12;
    
    return (todayMs / totalMs) * totalWidth;
  };

  // Calculate summary stats
  const stats = useMemo(() => {
    const total = phases.length;
    const completed = phases.filter(p => p.status === 'completed' || p.is_paid).length;
    const inProgress = phases.filter(p => p.status === 'in_progress').length;
    const progress = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, completed, inProgress, progress };
  }, [phases]);

  // Column widths
  const MONTH_WIDTH = 150; // Wide month columns
  const TIMELINE_WIDTH = MONTH_WIDTH * 12;

  if (phasesWithDates.length === 0) {
    return (
      <div className="space-y-4">
        {/* Stats Row - Single row of 4 cards, constrained to viewport */}
        <div className="flex gap-2 mb-3" style={{ maxWidth: 'calc(100vw - 18rem)' }}>
          <div className="flex-1 bg-slate-800 rounded-lg p-2 sm:p-3 border border-slate-700 overflow-hidden">
            <p className="text-slate-400 text-[10px] sm:text-xs truncate">Total Phases</p>
            <p className="text-white text-lg sm:text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="flex-1 bg-slate-800 rounded-lg p-2 sm:p-3 border border-slate-700 overflow-hidden">
            <div className="flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
              <p className="text-slate-400 text-[10px] sm:text-xs truncate">Completed</p>
            </div>
            <p className="text-green-400 text-lg sm:text-2xl font-bold">{stats.completed}</p>
          </div>
          <div className="flex-1 bg-slate-800 rounded-lg p-2 sm:p-3 border border-slate-700 overflow-hidden">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400" />
              <p className="text-slate-400 text-[10px] sm:text-xs truncate">In Progress</p>
            </div>
            <p className="text-amber-400 text-lg sm:text-2xl font-bold">{stats.inProgress}</p>
          </div>
          <div className="flex-1 bg-slate-800 rounded-lg p-2 sm:p-3 border border-slate-700 overflow-hidden">
            <p className="text-slate-400 text-[10px] sm:text-xs truncate">Overall Progress</p>
            <p className="text-blue-400 text-lg sm:text-2xl font-bold">{stats.progress}%</p>
          </div>
        </div>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg text-white">
              <Calendar className="w-5 h-5" />
              Project Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12 bg-slate-900/50 rounded-lg border-2 border-dashed border-slate-600">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-500" />
              <p className="text-slate-400 text-lg font-medium mb-2">No phases with dates to display</p>
              <p className="text-slate-500 text-sm">Add start dates to your phases to see them on the timeline.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const todayPosition = getTodayPosition(MONTH_WIDTH);

  return (
    <div className="space-y-4">
      {/* Stats Row - Single row of 4 cards, constrained to viewport */}
      <div className="flex gap-2 mb-3" style={{ maxWidth: 'calc(100vw - 18rem)' }}>
        <div className="flex-1 bg-slate-800 rounded-lg p-2 sm:p-3 border border-slate-700 overflow-hidden">
          <p className="text-slate-400 text-[10px] sm:text-xs truncate">Total Phases</p>
          <p className="text-white text-lg sm:text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="flex-1 bg-slate-800 rounded-lg p-2 sm:p-3 border border-slate-700 overflow-hidden">
          <div className="flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 flex-shrink-0" />
            <p className="text-slate-400 text-[10px] sm:text-xs truncate">Completed</p>
          </div>
          <p className="text-green-400 text-lg sm:text-2xl font-bold">{stats.completed}</p>
        </div>
        <div className="flex-1 bg-slate-800 rounded-lg p-2 sm:p-3 border border-slate-700 overflow-hidden">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400 flex-shrink-0" />
            <p className="text-slate-400 text-[10px] sm:text-xs truncate">In Progress</p>
          </div>
          <p className="text-amber-400 text-lg sm:text-2xl font-bold">{stats.inProgress}</p>
        </div>
        <div className="flex-1 bg-slate-800 rounded-lg p-2 sm:p-3 border border-slate-700 overflow-hidden">
          <p className="text-slate-400 text-[10px] sm:text-xs truncate">Overall Progress</p>
          <p className="text-blue-400 text-lg sm:text-2xl font-bold">{stats.progress}%</p>
        </div>
      </div>

      {/* Timeline Card */}
      <Card className="bg-slate-800 border-slate-700 overflow-hidden" style={{ maxWidth: 'calc(100vw - 18rem)' }}>
        <CardHeader className="pb-2 border-b border-slate-700">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg text-white">
                <Calendar className="w-5 h-5" />
                Project Timeline
              </CardTitle>
            </div>
            <div className="flex items-center gap-3 text-xs flex-wrap">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-gradient-to-r from-green-500 to-green-600 rounded"></div>
                <span className="text-slate-400">Completed</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-gradient-to-r from-amber-500 to-amber-600 rounded"></div>
                <span className="text-slate-400">In Progress</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded"></div>
                <span className="text-slate-400">Upcoming</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-gradient-to-r from-red-500 to-red-600 rounded"></div>
                <span className="text-slate-400">Delayed</span>
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-0">
          {/* Timeline Container with Fixed Left + Scrollable Right */}
          <div className="flex overflow-hidden">
            {/* FIXED LEFT COLUMNS */}
            <div className="flex-shrink-0 bg-slate-800 z-10 border-r-2 border-blue-500" style={{ minWidth: '384px' }}>
              {/* Header */}
              <div className="flex bg-blue-900">
                <div className="w-10 px-2 py-3 text-white text-xs font-semibold text-center border-r border-blue-800">#</div>
                <div className="w-44 px-3 py-3 text-white text-xs font-semibold border-r border-blue-800">Phase Name</div>
                <div className="w-24 px-3 py-3 text-white text-xs font-semibold border-r border-blue-800">Contractor</div>
                <div className="w-10 px-2 py-3 text-white text-xs font-semibold text-center">Info</div>
              </div>
              
              {/* Data Rows */}
              {phasesWithDates.map((phase, index) => {
                const isExpanded = expandedPhases[phase.id];
                const autoStatus = getPhaseStatus(phase);
                const isMaterial = phase.phase_type === 'material';
                const contractor = contractors.find(c => c.id === phase.contractor_id);
                
                return (
                  <div key={phase.id}>
                    <div 
                      className={`flex border-b border-slate-700/50 ${index % 2 === 0 ? 'bg-slate-800/80' : 'bg-slate-900/80'} hover:bg-slate-700/50 transition-colors`}
                      style={{ height: '52px' }}
                    >
                      <div className="w-10 px-2 flex items-center justify-center text-slate-500 text-sm border-r border-slate-700">
                        {index + 1}
                      </div>
                      <div className="w-44 px-3 flex items-center border-r border-slate-700">
                        <span className="text-white text-sm font-medium truncate" title={phase.name}>
                          {phase.name}
                        </span>
                      </div>
                      <div className="w-24 px-2 flex items-center border-r border-slate-700">
                        <span className="text-slate-400 text-xs truncate" title={isMaterial ? (phase.supplier_vendor || '-') : (phase.contractor_name || '-')}>
                          {isMaterial 
                            ? (phase.supplier_vendor ? `📦 ${phase.supplier_vendor}` : '-')
                            : (phase.contractor_name || '-')
                          }
                        </span>
                      </div>
                      <div className="w-10 px-2 flex items-center justify-center">
                        <button
                          onClick={() => togglePhaseExpanded(phase.id)}
                          className="p-1 hover:bg-slate-600 rounded transition-colors"
                          title={isExpanded ? 'Collapse details' : 'Expand details'}
                        >
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Expanded Phase Card */}
                    {isExpanded && (
                      <div className="bg-slate-900 border-b border-slate-600 p-3">
                        <div className={`rounded-lg p-3 ${isMaterial ? 'bg-gradient-to-r from-green-900/50 to-emerald-900/50 border border-green-700' : 'bg-gradient-to-r from-blue-900/50 to-indigo-900/50 border border-blue-700'}`}>
                          {/* Header with badges */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{isMaterial ? '📦' : '🔨'}</span>
                              <span className="text-white font-semibold">{phase.name}</span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${isMaterial ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'}`}>
                                {isMaterial ? 'Material' : 'Labor'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {phase.is_paid && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Paid
                                </span>
                              )}
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                autoStatus === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                                autoStatus === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                                autoStatus === 'scheduled' ? 'bg-purple-500/20 text-purple-400' :
                                'bg-slate-500/20 text-slate-400'
                              }`}>
                                {autoStatus === 'in_progress' ? 'In Progress' : autoStatus.charAt(0).toUpperCase() + autoStatus.slice(1)}
                              </span>
                            </div>
                          </div>
                          
                          {phase.description && (
                            <p className="text-slate-400 text-sm mb-3">{phase.description}</p>
                          )}
                          
                          {/* Cost Grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
                            <div className="bg-slate-800/50 rounded p-2">
                              <p className="text-slate-500 text-xs">Estimated</p>
                              <p className="text-white font-bold">${(phase.estimated_cost || 0).toLocaleString()}</p>
                              <p className="text-slate-500 text-xs">
                                L: ${(phase.estimated_labor || 0).toLocaleString()} | M: ${(phase.estimated_materials || 0).toLocaleString()}
                              </p>
                            </div>
                            <div className="bg-slate-800/50 rounded p-2">
                              <p className="text-slate-500 text-xs">Actual</p>
                              <p className="text-blue-400 font-bold">${(phase.actual_cost || 0).toLocaleString()}</p>
                              <p className="text-slate-500 text-xs">
                                L: ${(phase.actual_labor || 0).toLocaleString()} | M: ${(phase.actual_materials || 0).toLocaleString()}
                              </p>
                            </div>
                            <div className="bg-slate-800/50 rounded p-2">
                              <p className="text-slate-500 text-xs">Labor %</p>
                              <p className="text-white font-bold">
                                {phase.actual_cost > 0 ? `${((phase.actual_labor || 0) / phase.actual_cost * 100).toFixed(0)}%` : '0%'}
                              </p>
                            </div>
                            <div className="bg-slate-800/50 rounded p-2">
                              <p className="text-slate-500 text-xs">Cost/SqFt</p>
                              <p className="text-white font-bold">
                                {phase.exclude_from_sqft ? 'N/A' : 
                                  phase.actual_cost > 0 && project?.sqft ? `$${(phase.actual_cost / project.sqft).toFixed(2)}` : '$0.00'}
                              </p>
                            </div>
                            <div className="bg-slate-800/50 rounded p-2">
                              <p className="text-slate-500 text-xs">Variance</p>
                              <p className={`font-bold ${(phase.estimated_cost - phase.actual_cost) >= 0 ? 'text-emerald-400' : 'text-orange-400'}`}>
                                ${((phase.estimated_cost || 0) - (phase.actual_cost || 0)).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          
                          {/* Timeline */}
                          <div className="grid grid-cols-3 gap-3 text-sm">
                            {isMaterial ? (
                              <>
                                <div>
                                  <p className="text-slate-500 text-xs">Order Date</p>
                                  <p className="text-white">{phase.order_date ? formatDateFull(phase.order_date) : 'Not set'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">Delivery Date</p>
                                  <p className="text-white">
                                    {phase.actual_delivery_date ? formatDateFull(phase.actual_delivery_date) : 
                                     phase.estimated_delivery_date ? `${formatDateFull(phase.estimated_delivery_date)} (est)` : 'Not set'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">Supplier</p>
                                  <p className="text-white">{phase.supplier_vendor || 'Not set'}</p>
                                </div>
                              </>
                            ) : (
                              <>
                                <div>
                                  <p className="text-slate-500 text-xs">Start Date</p>
                                  <p className="text-white">{phase.start_date ? formatDateFull(phase.start_date) : 'Not set'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">End Date</p>
                                  <p className="text-white">{phase.end_date ? formatDateFull(phase.end_date) : 'Not completed'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500 text-xs">Contractor</p>
                                  <p className="text-white">{contractor?.company || contractor?.name || phase.contractor_name || 'Not assigned'}</p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* SCROLLABLE TIMELINE */}
            <div 
              className="overflow-x-scroll overflow-y-visible scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800" 
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div style={{ width: `${TIMELINE_WIDTH}px`, minWidth: `${TIMELINE_WIDTH}px` }}>
                {/* Month Headers */}
                <div className="flex bg-blue-900">
                  {monthsData.map((month, i) => (
                    <div 
                      key={i}
                      className="text-center py-3 text-white text-xs font-semibold border-r border-blue-800/50"
                      style={{ width: `${MONTH_WIDTH}px` }}
                    >
                      {month.label}
                    </div>
                  ))}
                </div>
                
                {/* Timeline Rows */}
                <div className="relative" style={{ overflow: 'visible' }}>
                  {/* Today Line */}
                  {todayPosition !== null && (
                    <div 
                      className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20 pointer-events-none"
                      style={{ left: `${todayPosition}px` }}
                    >
                      <div className="absolute top-1 left-1 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded font-semibold whitespace-nowrap">
                        Today
                      </div>
                    </div>
                  )}
                  
                  {phasesWithDates.map((phase, index) => {
                    const barPos = getBarPosition(phase, MONTH_WIDTH);
                    const colorClass = getPhaseColor(phase);
                    const statusLabel = getStatusLabel(phase);
                    const isHovered = hoveredPhase === phase.id;
                    
                    return (
                      <div 
                        key={phase.id}
                        className={`relative border-b border-slate-700/50 ${index % 2 === 0 ? 'bg-slate-800/30' : 'bg-slate-900/30'}`}
                        style={{ height: '52px', overflow: 'visible' }}
                      >
                        {/* Month Grid Lines */}
                        <div className="absolute inset-0 flex pointer-events-none">
                          {monthsData.map((_, i) => (
                            <div 
                              key={i}
                              className="border-r border-slate-700/30"
                              style={{ width: `${MONTH_WIDTH}px` }}
                            />
                          ))}
                        </div>
                        
                        {/* Phase Bar */}
                        <div
                          className={`absolute h-8 top-1/2 -translate-y-1/2 bg-gradient-to-r ${colorClass} rounded-md shadow-lg flex items-center px-3 cursor-pointer transition-all duration-200 ${isHovered ? 'scale-105 shadow-xl z-30' : 'z-10'}`}
                          style={{ 
                            left: `${barPos.left}px`, 
                            width: `${barPos.width}px`,
                          }}
                          onMouseEnter={() => setHoveredPhase(phase.id)}
                          onMouseLeave={() => setHoveredPhase(null)}
                        >
                          <span className="text-white text-xs font-medium truncate drop-shadow">
                            {statusLabel.icon && <span className="mr-1">{statusLabel.icon}</span>}
                            {phase.name}
                          </span>
                          
                          {/* Tooltip - Positioned BELOW the bar */}
                          {isHovered && (
                            <div 
                              className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-slate-900 border border-slate-600 rounded-lg shadow-2xl p-3 min-w-[220px] max-w-[280px] z-50"
                              style={{ pointerEvents: 'none' }}
                            >
                              {/* Arrow pointing up */}
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-8 border-transparent border-b-slate-900"></div>
                              
                              <div className="text-white font-semibold text-xs mb-2">{phase.name}</div>
                              
                              <div className="space-y-1 text-[11px]">
                                <div className="flex justify-between gap-4">
                                  <span className="text-slate-400">{phase.phase_type === 'material' ? 'Supplier:' : 'Contractor:'}</span>
                                  <span className="text-white text-right">{phase.phase_type === 'material' ? (phase.supplier_vendor || '-') : (phase.contractor_name || '-')}</span>
                                </div>
                                <div className="flex justify-between gap-4">
                                  <span className="text-slate-400">Duration:</span>
                                  <span className="text-white">{formatDate(phase.startDate)} - {formatDate(phase.endDate)}</span>
                                </div>
                                <div className="flex justify-between gap-4">
                                  <span className="text-slate-400">Budget:</span>
                                  <span className="text-white">${(phase.estimated_cost || 0).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between gap-4">
                                  <span className="text-slate-400">Actual:</span>
                                  <span className="text-white">${(phase.actual_cost || 0).toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between gap-4">
                                  <span className="text-slate-400">Status:</span>
                                  <span className={statusLabel.color}>{statusLabel.text}</span>
                                </div>
                              </div>
                              
                              {phase.description && (
                                <div className="mt-2 pt-2 border-t border-slate-700">
                                  <p className="text-slate-400 text-[10px] leading-relaxed line-clamp-2">{phase.description}</p>
                                </div>
                              )}
                              
                              {phase.category && (
                                <div className="mt-1">
                                  <span className="inline-block bg-slate-700 text-slate-300 text-[9px] px-1.5 py-0.5 rounded">
                                    {phase.category.replace(/_/g, ' ').toUpperCase()}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          
          {/* Scroll Hint */}
          <div className="text-center py-3 text-slate-500 text-xs border-t border-slate-700">
            ← Scroll horizontally to view more months →
          </div>
        </CardContent>
      </Card>

      {/* Read-only notice */}
      {readOnly && (
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
          <p className="text-blue-300 text-sm">
            <strong>📋 READ-ONLY VIEW</strong> — This page shows a snapshot of your project timeline. To edit phases, budgets, or manage draws, go to the <strong>Phases</strong> or <strong>Loan Details</strong> page.
          </p>
        </div>
      )}
    </div>
  );
};

export default GanttChart;
